/**
 * Product Card Quantity Component
 * Handles quantity increase/decrease buttons on product cards
 */
class ProductCardQuantity extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.decreaseButton = null;
    this.increaseButton = null;
    this.inputElement = null;
  }

  connectedCallback() {
    this.abortController = new AbortController();

    const quantityWrapper = this.querySelector('.product-card__quantity-wrapper');
    if (!quantityWrapper) return;

    this.decreaseButton = quantityWrapper.querySelector('.product-card__quantity-btn--minus');
    this.increaseButton = quantityWrapper.querySelector('.product-card__quantity-btn--plus');
    this.inputElement = quantityWrapper.querySelector('.product-card__quantity-input');

    if (!this.decreaseButton || !this.increaseButton || !this.inputElement) return;

    this.updateUI();

    this.decreaseButton.addEventListener('click', () => this.onDecreaseQuantity(), {
      signal: this.abortController.signal
    });

    this.increaseButton.addEventListener('click', () => this.onIncreaseQuantity(), {
      signal: this.abortController.signal
    });

    const form = this.closest('form');
    if (form) {
      form.addEventListener('variant:change', () => this.updateUI(), {
        signal: this.abortController.signal
      });
    }
  }

  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  onDecreaseQuantity() {
    if (!this.inputElement) return;
    const currentValue = parseInt(this.inputElement.value) || 1;
    const minValue = parseInt(this.inputElement.min) || 1;
    if (currentValue > minValue) {
      this.inputElement.value = currentValue - 1;
      this.updateUI();
    }
  }

  onIncreaseQuantity() {
    if (!this.inputElement) return;
    const currentValue = parseInt(this.inputElement.value) || 1;
    const maxValue = this.inputElement.hasAttribute('max')
      ? parseInt(this.inputElement.max)
      : null;
    if (!maxValue || currentValue < maxValue) {
      this.inputElement.value = currentValue + 1;
      this.updateUI();
    }
  }

  updateUI() {
    if (!this.inputElement || !this.decreaseButton || !this.increaseButton) return;
    const currentValue = parseInt(this.inputElement.value) || 1;
    const minValue = parseInt(this.inputElement.min) || 1;
    const maxValue = this.inputElement.hasAttribute('max')
      ? parseInt(this.inputElement.max)
      : null;
    this.decreaseButton.disabled = currentValue <= minValue;
    this.increaseButton.disabled = maxValue !== null && currentValue >= maxValue;
  }

  get quantity() {
    return this.inputElement ? parseInt(this.inputElement.value) || 1 : 1;
  }

  set quantity(value) {
    if (this.inputElement) {
      const numValue = parseInt(value) || 1;
      const minValue = parseInt(this.inputElement.min) || 1;
      const maxValue = this.inputElement.hasAttribute('max')
        ? parseInt(this.inputElement.max)
        : null;
      let clampedValue = Math.max(minValue, numValue);
      if (maxValue !== null) clampedValue = Math.min(maxValue, clampedValue);
      this.inputElement.value = clampedValue;
      this.updateUI();
    }
  }
}

if (!window.customElements.get('product-card-quantity')) {
  window.customElements.define('product-card-quantity', ProductCardQuantity);
}

(function() {
  'use strict';
  const originalErrorHandler = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    if (source && source.includes('theme.js') &&
        message && (message.includes('private field') || message.includes('Cannot read'))) {
      if (error && error.stack && error.stack.includes('onScrollEnd')) {
        return true;
      }
    }
    if (originalErrorHandler) {
      return originalErrorHandler.call(this, message, source, lineno, colno, error);
    }
    return false;
  };

  window.addEventListener('unhandledrejection', function(event) {
    if (event.reason && event.reason.message &&
        event.reason.message.includes('private field') &&
        event.reason.stack && event.reason.stack.includes('onScrollEnd')) {
      event.preventDefault();
    }
  });

  function setupCarouselFix(carousel) {
    if (carousel._scrollEndFixApplied) return;
    carousel._scrollEndFixApplied = true;
    carousel.addEventListener('scrollend', function() {
      const pagination = carousel.parentElement?.querySelector('.product-card__pagination');
      if (pagination) {
        const items = Array.from(carousel.querySelectorAll('.product-card__media-item:not([hidden])'));
        if (items.length > 0) {
          const scrollLeft = carousel.scrollLeft;
          const itemWidth = carousel.clientWidth || items[0].offsetWidth;
          const currentIndex = Math.min(Math.max(0, Math.round(scrollLeft / itemWidth)), items.length - 1);
          const dots = pagination.querySelectorAll('.tap-area');
          dots.forEach((dot, index) => {
            dot.setAttribute('aria-current', index === currentIndex ? 'true' : 'false');
          });
        }
      }
    }, { passive: true });
  }

  function initCarousels() {
    document.querySelectorAll('scroll-carousel.product-card__carousel').forEach(setupCarouselFix);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCarousels);
  } else {
    initCarousels();
  }

  const observer = new MutationObserver(initCarousels);
  observer.observe(document.body, { childList: true, subtree: true });
})();

(function() {
  'use strict';
  function ensureCarouselItemsVisible() {
    document.querySelectorAll('.product-card__carousel .product-card__media-item').forEach(item => {
      if (item.hasAttribute('hidden')) item.removeAttribute('hidden');
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensureCarouselItemsVisible);
  } else {
    ensureCarouselItemsVisible();
  }
  const observer = new MutationObserver(ensureCarouselItemsVisible);
  observer.observe(document.body, { childList: true, subtree: true });
})();


(function () {
  'use strict';

  document.addEventListener('variant:change', function (event) {
    const variant = event.detail && event.detail.variant;
    if (!variant) return;

    const form = event.target && event.target.tagName === 'FORM'
      ? event.target
      : (typeof event.detail.formId === 'string' ? document.getElementById(event.detail.formId) : null);
    const card = form && form.closest('product-card');
    const badge = card && card.querySelector('.product-badge-variant-counts .c-badge');

    if (badge) {
      const inStockLabel = badge.getAttribute('data-label-in-stock') || 'In stock';
      const outOfStockLabel = badge.getAttribute('data-label-out-of-stock') || 'Out of stock';
      const inStock = variant.available === true;

      badge.textContent = inStock ? inStockLabel : outOfStockLabel;
      badge.classList.toggle('c-badge--out-of-stock', !inStock);
    }

    const modal = document.querySelector('quick-buy-modal');
    if (!modal) return;

    const container = modal.querySelector('[data-variant-image-container]');
    if (!container) return;

    const currentImg = container.querySelector('.qb-new__thumb-img');
    if (!currentImg) return;

    const productId = event.detail.formId
      ? event.detail.formId.toString().replace(/\D/g, '').slice(0, -1)
      : null;

    const dataScript = modal.querySelector('script[id^="qb-new-media-"]');
    if (!dataScript) return;

    let mediaMap;
    try {
      mediaMap = JSON.parse(dataScript.textContent);
    } catch (e) {
      return;
    }

    const newSrc = mediaMap[variant.id];
    if (!newSrc) return;

    const baseSrc = newSrc.split('?')[0];

    if (currentImg.src && currentImg.src.includes(baseSrc.split('/').pop().split('_')[0])) return;

    currentImg.style.transition = 'opacity 0.15s ease';
    currentImg.style.opacity = '0';

    const newImg = new Image();
    newImg.className = currentImg.className;
    newImg.alt = variant.featured_media ? (variant.featured_media.alt || '') : currentImg.alt;
    newImg.sizes = '80px';
    newImg.srcset = [
      baseSrc + '?width=80 80w',
      baseSrc + '?width=160 160w',
      baseSrc + '?width=240 240w',
    ].join(', ');
    newImg.src = baseSrc + '?width=160';
    newImg.dataset.mediaId = variant.featured_media ? variant.featured_media.id : '';

    setTimeout(function () {
      container.replaceChild(newImg, currentImg);
      newImg.style.opacity = '0';
      newImg.style.transition = 'opacity 0.3s ease';
      setTimeout(function () {
        newImg.style.opacity = '1';
      }, 10);
    }, 150);
  });
})();

